/**
 * Flow Service
 *
 * Manages saved workflow flows:
 * - Save flow configurations
 * - Load saved flows
 * - List available flows
 * - Delete flows
 */

import { getAPIBaseUrl } from '../environment.js'

/**
 * Save a workflow flow
 * @param {string} name - User-defined flow name
 * @param {string} accountId - Scout account ID
 * @param {string} applicationName - Application name
 * @param {Object} flowData - ReactFlow state (nodes, edges)
 * @param {Object} jobMappings - Mapping of node IDs to job IDs
 * @returns {Promise<Object>} Save response with flow_id
 */
export async function saveFlow(name, accountId, applicationName, flowData, jobMappings = {}) {
  const url = `${getAPIBaseUrl()}/flows/save`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name,
      account_id: accountId,
      application_name: applicationName,
      flow_data: flowData,
      job_mappings: jobMappings,
    }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(`Failed to save flow: ${error.detail || response.status}`)
  }

  return response.json()
}

/**
 * List all saved flows, optionally filtered by account
 * @param {string} accountId - Optional account ID filter
 * @returns {Promise<Object>} List response with flows array
 */
export async function listFlows(accountId = null) {
  let url = `${getAPIBaseUrl()}/flows/list`
  if (accountId) {
    url += `?account_id=${encodeURIComponent(accountId)}`
  }

  console.log(' flowService: Fetching flows from:', url)
  const response = await fetch(url)

  if (!response.ok) {
    console.error(' flowService: Failed to fetch flows:', response.status)
    throw new Error(`Failed to list flows: ${response.status}`)
  }

  const data = await response.json()
  console.log(' flowService: Received flows:', data)
  return data
}

/**
 * Get a saved flow by ID
 * @param {string} flowId - Flow ID to retrieve
 * @returns {Promise<Object>} Flow detail with job statuses
 */
export async function getFlow(flowId) {
  const url = `${getAPIBaseUrl()}/flows/${flowId}`

  const response = await fetch(url)

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Flow ${flowId} not found`)
    }
    throw new Error(`Failed to get flow: ${response.status}`)
  }

  return response.json()
}

/**
 * Delete a saved flow
 * @param {string} flowId - Flow ID to delete
 * @returns {Promise<Object>} Delete response
 */
export async function deleteFlow(flowId) {
  const url = `${getAPIBaseUrl()}/flows/${flowId}`

  const response = await fetch(url, {
    method: 'DELETE',
  })

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Flow ${flowId} not found`)
    }
    throw new Error(`Failed to delete flow: ${response.status}`)
  }

  return response.json()
}

/**
 * Update a flow's name
 * @param {string} flowId - Flow ID to update
 * @param {string} newName - New name for the flow
 * @returns {Promise<Object>} Update response
 */
export async function updateFlowName(flowId, newName) {
  const url = `${getAPIBaseUrl()}/flows/${flowId}/name`

  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: newName,
    }),
  })

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Flow ${flowId} not found`)
    }
    throw new Error(`Failed to update flow name: ${response.status}`)
  }

  return response.json()
}
